/**
 * WS client for the Lokma harness server (Fastify `/ws/:sessionId`).
 * Single WebSocket entry point for the web app — all panes go through here (DRY).
 *
 * Source of truth for frame shapes is `lokma-shared` (Zod schemas) — this file
 * never hand-duplicates frame names, it only adds browser conveniences:
 * URL building (vite `/ws` proxy first, direct fallback), auth attach,
 * reconnect backoff, safe decode, and a pure UI reducer over server frames.
 */
import {
  ClientMessageSchema,
  ServerMessageSchema,
  type ClientFreeRecordRow,
  type ClientMessage,
  type PromptFile,
  type PromptImage,
  type ReasoningEffort,
  type ServerMessage,
} from '@lokma/shared/protocol/ws';

export type { ClientMessage, ClientFreeRecordRow, PromptFile, PromptImage, ReasoningEffort, ServerMessage };

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

/** Server frames the permission flow produces (narrowed for UI queues). */
export type PermissionRequest = Extract<ServerMessage, { type: 'permission_request' }>;
export type QuestionRequest = Extract<ServerMessage, { type: 'ask_user_question' }>;
/** Live shell output + exit frames behind the TerminalPane (W3-10). */
export type TerminalDataFrame = Extract<ServerMessage, { type: 'terminal/data' }>;
export type TerminalExitFrame = Extract<ServerMessage, { type: 'terminal/exit' }>;
/** Agent-driven UI request (REQ-057) — queued for the shell consumer. */
export type UiActionRequest = Extract<ServerMessage, { type: 'ui_action' }>;
/**
 * REQ-149: session data over the socket — a `sessions` snapshot/push feeds the
 * sidebar, `transcript` answers a `transcript_get`, and `transcript_append`
 * announces one freshly persisted row (no REST reload needed).
 */
export type SessionsFrame = Extract<ServerMessage, { type: 'sessions' }>;
export type TranscriptFrame = Extract<ServerMessage, { type: 'transcript' }>;
export type TranscriptAppendFrame = Extract<ServerMessage, { type: 'transcript_append' }>;
export type ToolCallEntry = {
  tool: string;
  input: unknown;
  result?: unknown;
  isError?: boolean;
};
export type CostTotal = {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  model: string;
  /**
   * REQ-174: thinking level of the LATEST cost frame — what the run meta
   * line displays as `thinking: <level>`. Absent for engine frames that
   * carry no level.
   */
  reasoningEffort?: ReasoningEffort;
};

/** Reducer state derived from the append-only server frame log. */
export type WsUiState = {
  stream: string;
  /** Live reasoning text (REQ-050) — shown while streaming, never persisted. */
  thinking: string;
  /**
   * REQ-174: thinking deltas seen since the last cost frame — the raw signal
   * behind the honesty verdict below.
   */
  thinkingSinceCost: boolean;
  /**
   * REQ-174: did the last run that ASKED for reasoning actually publish any?
   * `null` when the last run did not ask (off/engine), so the meta line never
   * claims something the run did not promise.
   */
  reasoningPublished: boolean | null;
  toolCalls: Record<string, ToolCallEntry>;
  /**
   * REQ-111: stream cut recorded at each `tool_start` (`at` = stream length
   * when the call arrived). The view slices the live stream at these marks so
   * tool rows render interleaved in arrival order (text → tool → text → tool)
   * instead of one block pinned under the message. Explicit array (not key
   * order) — integer-like callIds would sort numerically in a Record.
   */
  toolMarks: Array<{ callId: string; at: number }>;
  cost: CostTotal;
  permissions: PermissionRequest[];
  questions: QuestionRequest[];
  /** Agent UI-control queue (REQ-057) — the shell opens panes per entry. */
  uiActions: UiActionRequest[];
  /** Latest auto-retry notice (REQ-077) — toast/badge only, never persisted. */
  retry: { attempt: number; maxAttempts: number; waitMs: number; message: string } | null;
  done: boolean;
  doneReason: string | null;
  lastError: string | null;
  /**
   * REQ-136: machine code of the last `error` frame (`turn_limit`, `aborted`,
   * upstream codes…). The UI picks its wording from this — a paused run is not
   * a failed send.
   */
  lastErrorCode: string | null;
};

/** Max auto-reconnect attempts before the hook gives up with status `error`. */
export const MAX_RECONNECT_ATTEMPTS = 10;

/** Harness server port for non-proxied environments (dev direct, debugging). */
const DIRECT_PORT = '3456';
const TOKEN_KEY = 'lokma-token';

function readToken(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Primary WS URL — relative to the serving origin so it rides the vite `/ws`
 * proxy in dev (`:3457` → `:3456`) and nginx in prod. Never hardcode `:3456`
 * here: a hardcoded port bypasses the proxy and breaks behind HTTPS/nginx.
 */
export function wsUrl(sessionId: string): string {
  if (typeof window === 'undefined' || !window.location) {
    return `ws://127.0.0.1:${DIRECT_PORT}/ws/${encodeURIComponent(sessionId)}`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/${encodeURIComponent(sessionId)}`;
}

/** Direct-to-server URL — fallback for environments without an `/ws` proxy. */
export function directWsUrl(sessionId: string): string {
  if (typeof window === 'undefined' || !window.location) {
    return `ws://127.0.0.1:${DIRECT_PORT}/ws/${encodeURIComponent(sessionId)}`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.hostname}:${DIRECT_PORT}/ws/${encodeURIComponent(sessionId)}`;
}

/** Attach the Bearer token as `?token=` when the user logged in via `lokma auth`. */
export function withAuthToken(url: string): string {
  const token = readToken();
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

/** Capped exponential backoff between reconnect attempts (deterministic, testable). */
export function reconnectDelay(attempt: number): number {
  const step = Math.max(0, Math.floor(attempt));
  return Math.min(500 * 2 ** step, 10_000);
}

/** Safely parse one incoming socket payload into a typed server frame. */
export function decodeServerFrame(data: unknown): ServerMessage | null {
  try {
    const text = typeof data === 'string' ? data : JSON.stringify(data);
    const parsed: unknown = JSON.parse(text);
    const result = ServerMessageSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

// ─── Client message builders (validated by construction, no drift) ───────────

function checked(msg: ClientMessage): string {
  return JSON.stringify(ClientMessageSchema.parse(msg));
}

/** Start/continue the transcript with a user prompt. */
export function promptMessage(
  prompt: string,
  sessionId?: string,
  opts: { model?: string; contextPaths?: string[]; reasoningEffort?: ReasoningEffort; images?: PromptImage[]; files?: PromptFile[] } = {},
): string {
  return checked({
    type: 'prompt',
    prompt,
    sessionId,
    model: opts.model,
    contextPaths: opts.contextPaths,
    reasoningEffort: opts.reasoningEffort,
    images: opts.images && opts.images.length > 0 ? opts.images : undefined,
    files: opts.files && opts.files.length > 0 ? opts.files : undefined,
  });
}

/** Stop the running stream (server answers with `done/aborted`). */
export function abortMessage(sessionId: string): string {
  return checked({ type: 'abort', sessionId });
}

/** Answer a `permission_request` frame (`always` persists a rule server-side). */
export function permissionAnswer(
  requestId: string,
  decision: 'allow' | 'deny' | 'always',
): string {
  return checked({ type: 'permission_response', requestId, decision });
}

/** Answer an `ask_user_question` frame (unblocks the stream). */
export function questionAnswer(requestId: string, answer: string): string {
  return checked({ type: 'ask_response', requestId, answer });
}

/**
 * REQ-182: answer an `open_project` confirmation frame — resolves the
 * agent's wait with the outcome the user actually chose ('done' = the
 * modal's Open button, 'cancelled' = the user dismissed it). The project
 * record itself was created before the frame shipped; this only reports
 * how the modal interaction went.
 */
export function projectAckMessage(actionId: string, outcome: 'done' | 'cancelled'): string {
  return checked({ type: 'project_ack', actionId, outcome });
}

/** Write stdin bytes to a live shell (server answers with `terminal/data`). */
export function terminalInput(terminalId: string, data: string): string {
  return checked({ type: 'terminal/input', terminalId, data });
}

/** Record the pane size for a live shell (stored server-side). */
export function terminalResize(terminalId: string, cols: number, rows: number): string {
  return checked({ type: 'terminal/resize', terminalId, cols, rows });
}

/** End a live shell (server confirms with `terminal/exit`). */
export function terminalKill(terminalId: string): string {
  return checked({ type: 'terminal/kill', terminalId });
}

/**
 * REQ-149: ask for the sidebar rows over the socket. The server answers with a
 * `sessions` snapshot AND keeps pushing fresh snapshots as sessions change —
 * the caller stops being a poller and becomes a subscriber.
 */
export function sessionsListMessage(): string {
  return checked({ type: 'sessions_list' });
}

/**
 * REQ-149: ask for one session's transcript over the socket. The answer is a
 * `transcript` snapshot; every later persisted row arrives as
 * `transcript_append` on the same socket.
 */
export function transcriptGetMessage(sessionId: string): string {
  return checked({ type: 'transcript_get', sessionId });
}

/**
 * REQ-205: persist the rows of a browser-side KEYLESS run (OmniRoute free
 * pool — no API key anywhere) into the session transcript through the
 * server's own store. The server answers with `transcript_recorded`
 * (`count` 0 = the identical batch was already persisted — success on a
 * retry after a lost ack).
 */
export function transcriptRecordMessage(
  sessionId: string,
  rows: ClientFreeRecordRow[],
): string {
  return checked({ type: 'transcript_record', sessionId, origin: 'client-free', rows });
}

// ─── Pure UI reducer (React state derives from this, unit-tested) ────────────

export function initialWsUiState(): WsUiState {
  return {
    stream: '',
    thinking: '',
    thinkingSinceCost: false,
    reasoningPublished: null,
    toolCalls: {},
    toolMarks: [],
    cost: { inputTokens: 0, outputTokens: 0, costUsd: 0, model: '' },
    permissions: [],
    questions: [],
    uiActions: [],
    retry: null,
    done: false,
    doneReason: null,
    lastError: null,
    lastErrorCode: null,
  };
}

function addCost(total: CostTotal, msg: Extract<ServerMessage, { type: 'cost' }>): CostTotal {
  return {
    inputTokens: total.inputTokens + msg.inputTokens,
    outputTokens: total.outputTokens + msg.outputTokens,
    costUsd: total.costUsd + msg.costUsd,
    model: msg.model || total.model,
    // REQ-174: last frame wins — the meta line reads the latest run's level.
    reasoningEffort: msg.reasoningEffort ?? total.reasoningEffort,
  };
}

/**
 * Fold one validated server frame into UI state.
 * Append-only: frames are never mutated, history lives in the hook's log.
 */
export function applyServerFrame(state: WsUiState, msg: ServerMessage): WsUiState {
  switch (msg.type) {
    case 'text_delta':
      return { ...state, stream: state.stream + msg.delta, done: false };
    case 'thinking_delta':
      return { ...state, thinking: state.thinking + msg.delta, thinkingSinceCost: true, done: false };
    case 'tool_start': {
      // REQ-111: cut the live stream here so the row interleaves in arrival
      // order. Idempotent — a resent start never double-marks the same call.
      const marked = state.toolMarks.some((m) => m.callId === msg.callId);
      return {
        ...state,
        toolCalls: { ...state.toolCalls, [msg.callId]: { tool: msg.tool, input: msg.input } },
        toolMarks: marked ? state.toolMarks : [...state.toolMarks, { callId: msg.callId, at: state.stream.length }],
      };
    }
    case 'tool_result': {
      const prev = state.toolCalls[msg.callId];
      if (!prev) return state;
      return {
        ...state,
        toolCalls: {
          ...state.toolCalls,
          [msg.callId]: { ...prev, result: msg.result, isError: msg.isError },
        },
      };
    }
    case 'permission_request':
      return { ...state, permissions: [...state.permissions, msg] };
    case 'ask_user_question':
      return { ...state, questions: [...state.questions, msg] };
    case 'ui_action':
      return { ...state, uiActions: [...state.uiActions, msg] };
    case 'retry_notice':
      // REQ-077: stream died, backing off — surface as toast/badge; the run
      // continues, so stream/done stay untouched.
      return {
        ...state,
        retry: { attempt: msg.attempt, maxAttempts: msg.maxAttempts, waitMs: msg.waitMs, message: msg.message },
      };
    case 'cost': {
      const cost = addCost(state.cost, msg);
      // REQ-174 honesty verdict: a run that ASKED for reasoning but streamed
      // none gets called out in the meta line instead of letting the user
      // assume the picker is broken. Runs that did not ask carry no verdict.
      const asked = cost.reasoningEffort !== undefined && cost.reasoningEffort !== 'off';
      return {
        ...state,
        cost,
        reasoningPublished: asked ? state.thinkingSinceCost : null,
        thinkingSinceCost: false,
      };
    }
    case 'agent_state':
      return state;
    case 'loop':
      // REQ-202: loop changes reach the console through the `loop-frame`
      // bridge (dispatched by the socket layer), not through chat state.
      return state;
    case 'terminal/data':
    case 'terminal/exit':
      // Terminal traffic belongs to the TerminalPane (it reads the same
      // frame log) — chat state never changes on shell output.
      return state;
    case 'sessions':
    case 'transcript':
    case 'transcript_append':
      // REQ-149: session data belongs to the session store (sidebar rows +
      // transcript cache, folded there via `applyWsEvent`) — the chat UI
      // state never changes on it.
      return state;
    case 'transcript_recorded':
      // REQ-205: ack for a client-free record — the awaiting caller (the
      // chat's free-run path) keys on it; chat state is untouched.
      return state;
    case 'done':
      return {
        ...state,
        done: true,
        doneReason: msg.reason,
        // REQ-174: an aborted run gets no cost frame — its partial thinking
        // must not leak a "published" verdict into the next run.
        thinkingSinceCost: msg.reason === 'complete' ? state.thinkingSinceCost : false,
      };
    case 'error':
      // REQ-038: an upstream failure used to leave the chat stuck on
      // "sending…" forever — the server sends `error` with NO following
      // `done`, and pending rows only clear on `done`. Ending the run here
      // drops the optimistic row; the message stays visible via lastError.
      return {
        ...state,
        lastError: msg.message,
        lastErrorCode: msg.code ?? null,
        done: true,
        doneReason: 'error',
        thinkingSinceCost: false,
      };
  }
}

/**
 * REQ-132: drop the live trace once the finished transcript has been
 * refetched. The persisted row and the live buffer describe the same answer,
 * so keeping both on screen rendered every reply — and its thinking — twice.
 * Cost/permissions/questions survive: they are not part of the answer text.
 */
export function dropLiveTrace(state: WsUiState): WsUiState {
  return { ...state, stream: '', thinking: '', toolCalls: {}, toolMarks: [], retry: null };
}

/** Remove an answered request from its queue (by requestId). */
export function dropRequest<T extends { requestId: string }>(queue: T[], requestId: string): T[] {
  return queue.filter((item) => item.requestId !== requestId);
}
